import { useEffect, useState, useCallback } from 'react';
import { notificationManager, getNotificationStatus, requestNotificationPermission } from '../utils/notificationUtils';

export interface NotificationState {
  supported: boolean;
  permission: NotificationPermission;
  canNotify: boolean;
  initialized: boolean;
}

export interface UseNotificationsReturn {
  notificationState: NotificationState;
  requestPermission: () => Promise<NotificationPermission>;
  notifyQueryComplete: (query: string, response: string) => Promise<void>;
  notifyError: (error: string) => Promise<void>;
  refreshStatus: () => void;
}

/**
 * Hook for managing browser notifications in the Shipment CoCaptain app
 */
export function useNotifications(): UseNotificationsReturn {
  const [notificationState, setNotificationState] = useState<NotificationState>({
    supported: false,
    permission: 'default',
    canNotify: false,
    initialized: false,
  });

  // Refresh notification status
  const refreshStatus = useCallback(() => {
    const status = getNotificationStatus();
    setNotificationState(status);
  }, []);

  // Initialize and monitor notification status
  useEffect(() => {
    // Initial status check
    refreshStatus();

    // Listen for visibility changes to refresh status
    const handleVisibilityChange = () => {
      refreshStatus();
    };

    // Listen for focus changes to refresh status
    const handleFocus = () => {
      refreshStatus();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleFocus);

    // Cleanup listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleFocus);
    };
  }, [refreshStatus]);

  // Wrapped functions that also refresh status
  const wrappedRequestPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    refreshStatus();
    return permission;
  }, [refreshStatus]);

  const wrappedNotifyQueryComplete = useCallback(async (query: string, response: string) => {
    await notificationManager.notifyQueryComplete(query, response);
    refreshStatus();
  }, [refreshStatus]);

  const wrappedNotifyError = useCallback(async (error: string) => {
    await notificationManager.notifyError(error);
    refreshStatus();
  }, [refreshStatus]);

  return {
    notificationState,
    requestPermission: wrappedRequestPermission,
    notifyQueryComplete: wrappedNotifyQueryComplete,
    notifyError: wrappedNotifyError,
    refreshStatus,
  };
}
